import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { ScrollToTop } from "./components/common/ScrollToTop";
import UmbrellaHome from "./pages/UmbrellaHome";
import HomePage from "./pages/HomePage";
import SolutionsPage from "./pages/SolutionsPage";
import GrowthFactorPage from "./pages/GrowthFactorPage";
import CreativePage from "./pages/CreativePage";
import CoffeePage from "./pages/CoffeePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ConsultingInsights from "./pages/ConsultingInsights";
import CreativeStories from "./pages/CreativeStories";
import ArticleReader from "./components/blog/ArticleReader";
import DentalLayout from "./dental/DentalLayout";
import DentalHome from "./dental/pages/Home";
import DentalTreatment from "./dental/pages/TreatmentPage";
import DentalTeen from "./dental/pages/TeenSmile";
import DentalDigital from "./dental/pages/DigitalDentistry";
import DentalInternational from "./dental/pages/InternationalPage";
import DentalKolum from "./dental/pages/Kolum";
import DentalArticle from "./dental/pages/Article";
import DentalDoctor from "./dental/pages/DoctorPage";
import DentalStories from "./dental/pages/PatientStories";
import DentalAbout from "./dental/pages/About";
import DentalContact from "./dental/pages/Contact";
import DentalLegal from "./dental/pages/LegalPage";
import DentalNotFound from "./dental/pages/NotFound";
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import Overview from "./admin/Overview";
import Opportunities from "./admin/Opportunities";
import Leads from "./admin/Leads";
import Panels from "./admin/Panels";
import Content from "./admin/Content";
import Media from "./admin/Media";
import Settings from "./admin/Settings";
import AiDiscovery from "./admin/AiDiscovery";
import IdeaHistory from "./admin/IdeaHistory";
import StudioDashboard from "./admin/StudioDashboard";
import StudioClients from "./admin/StudioClients";
import StudioClientDetail from "./admin/StudioClientDetail";
import StudioAppointments from "./admin/StudioAppointments";
import StudioPage from "./studio/StudioPage";
import StudioEnquiryPage from "./studio/StudioEnquiryPage";
import StudioAgreementPage from "./studio/StudioAgreementPage";
import Stub from "./admin/Stub";
import { AiraWidget } from "./components/aira/AiraWidget";
import { AnalyticsTracker } from "./components/analytics/AnalyticsTracker";

const PublicLayout = () => (
  <>
    <Navbar />
    <div className="pt-20">
      <Outlet />
    </div>
    <Footer />
    <AiraWidget />
  </>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <Routes>
          <Route path="/admin" element={<AdminLogin />} />
          <Route element={<AdminLayout />}>
            <Route path="/admin/overview" element={<Overview />} />
            <Route path="/admin/opportunities" element={<Opportunities />} />
            <Route path="/admin/leads" element={<Leads />} />
            <Route path="/admin/panels" element={<Panels />} />
            <Route path="/admin/content" element={<Content />} />
            <Route path="/admin/media" element={<Media />} />
            <Route path="/admin/settings" element={<Settings />} />
            <Route path="/admin/ai-discovery" element={<AiDiscovery />} />
            <Route path="/admin/idea-history" element={<IdeaHistory />} />
            <Route path="/admin/studio" element={<StudioDashboard />} />
            <Route path="/admin/studio/clients" element={<StudioClients />} />
            <Route path="/admin/studio/clients/:id" element={<StudioClientDetail />} />
            <Route path="/admin/studio/appointments" element={<StudioAppointments />} />
            <Route path="/admin/conversations" element={<Stub area="conversations" />} />
            <Route path="/admin/conversions" element={<Stub area="conversions" />} />
            <Route path="/admin/revenue" element={<Stub area="revenue" />} />
            <Route path="/admin/analytics" element={<Stub area="analytics" />} />
          </Route>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<UmbrellaHome />} />
            <Route path="/consulting" element={<HomePage />} />
          <Route path="/consulting/digital-studio" element={<StudioPage />} />
          <Route path="/consulting/digital-studio/enquire" element={<StudioEnquiryPage />} />
          <Route path="/studio/agreement/:token" element={<StudioAgreementPage />} />
            <Route path="/consulting/solutions" element={<SolutionsPage />} />
            <Route path="/consulting/growth-factor" element={<GrowthFactorPage />} />
            <Route path="/consulting/insights" element={<ConsultingInsights />} />
            <Route path="/consulting/insights/:slug" element={<ArticleReader vertical="consulting" basePath="/consulting/insights" channelName="Growth · Insights" />} />
            <Route path="/creative" element={<CreativePage />} />
            <Route path="/creative/stories" element={<CreativeStories />} />
            <Route path="/creative/stories/:slug" element={<ArticleReader vertical="creative" basePath="/creative/stories" channelName="Stories" />} />
            <Route path="/coffee-with-dr-irma" element={<CoffeePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/solutions" element={<Navigate to="/consulting/solutions" replace />} />
            <Route path="/growth-factor" element={<Navigate to="/consulting/growth-factor" replace />} />
            <Route path="/books" element={<Navigate to="/creative" replace />} />
            <Route path="/work-with-us" element={<Navigate to="/contact" replace />} />
          </Route>
          <Route path="/dental" element={<Navigate to="/dental/ms" replace />} />
          <Route path="/dental/:lang" element={<DentalLayout />}>
            <Route index element={<DentalHome />} />
            <Route path="treatments/:slug" element={<DentalTreatment />} />
            <Route path="teen-smile" element={<DentalTeen />} />
            <Route path="digital-dentistry" element={<DentalDigital />} />
            <Route path="international" element={<DentalInternational mode="hub" />} />
            <Route path="international/singapore" element={<DentalInternational mode="singapore" />} />
            <Route path="international/indonesia-batam" element={<DentalInternational mode="indonesia" />} />
            <Route path="kolum" element={<DentalKolum />} />
            <Route path="kolum/:slug" element={<DentalArticle />} />
            <Route path="doctors/:slug" element={<DentalDoctor />} />
            <Route path="patient-stories" element={<DentalStories />} />
            <Route path="about" element={<DentalAbout />} />
            <Route path="contact" element={<DentalContact />} />
            <Route path="privacy" element={<DentalLegal key="privacy" mode="privacy" />} />
            <Route path="disclaimer" element={<DentalLegal key="disclaimer" mode="disclaimer" />} />
            <Route path="*" element={<DentalNotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
